import React, { useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Grid,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import config from "../config/Config";
import { formatDate, getAppliedJobDetails } from "../helper/My_Helper";

const EPFOForm11 = ({ candidateData = {} }) => {
  const formRef = useRef();
  const doj= candidateData?.annexure_eleven_form_data?.candidate_doj;
  const appliedJob = getAppliedJobDetails(candidateData);
  // Extract data from the API response with safe fallbacks
  const extractedData = useMemo(() => {
    const applicantForm = candidateData?.applicant_form_data || {};
    const annexureEleven = candidateData?.annexure_eleven_form_data || {};
    const fullName = applicantForm.full_name || {};
    
    // Format date for joining (DD-MM-YYYY)
    const formatJoiningDate = (dateStr) => {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return "";
      }
    };

    // Format DOB from separate fields
    const formatDOB = () => {
      const date = applicantForm.dob?.date || "";
      const month = applicantForm.dob?.month || "";
      const year = applicantForm.dob?.year || "";
      if (!date && !month && !year) return "";
      
      // If month is text, convert to number
      let monthNum = month;
      if (isNaN(month)) {
        const monthMap = {
          "january": "01", "february": "02", "march": "03", "april": "04",
          "may": "05", "june": "06", "july": "07", "august": "08",
          "september": "09", "october": "10", "november": "11", "december": "12"
        };
        monthNum = monthMap[month.toLowerCase()] || "";
      }
      
      const day = String(date).padStart(2, "0");
      const monthStr = String(monthNum).padStart(2, "0");
      return `${day}/${monthStr}/${year}`;
    };

    return {
      // Personal Details
      firstName: fullName.first_name || "",
      middleName: fullName.middle_name || "",
      surname: fullName.surname || "",
      fullName: [fullName.first_name, fullName.middle_name, fullName.surname]
        .filter(Boolean)
        .join(" ") || candidateData?.name || "",
      
      // Date of Birth
      dob: applicantForm.dob || {},
      dobFormatted: formatDOB(),
      
      // Father/Husband Name
      fatherHusbandName: applicantForm.father_hushband_name || "",
      
      // Gender & Marital Status
      gender: applicantForm.gender || "",
      maritalStatus: applicantForm.marital_status || "",
      
      // Email & Mobile
      email: applicantForm.communication_address?.email_id || candidateData?.email || "",
      mobileNo: applicantForm.communication_address?.mobile_no || candidateData?.mobile_no || "",
      
      // Bank Details
      bankName: annexureEleven.bank_details?.bank_name || "",
      branchIfsc: annexureEleven.bank_details?.branch_ifsc || "",
      accountNo: annexureEleven.bank_details?.account_no || "",
      
      // Pan Number
      panNumber: annexureEleven.pan_number || "",
      
      // Aadhar Number
      aadharNumber: applicantForm.aadhar_number || "",
      
      // Previous Organization Details
      previousOrg: annexureEleven.previous_organization_details || {},
      
      // Joining Date
      joiningDate: annexureEleven?.candidate_doj || "",
      joiningDateFormatted: formatJoiningDate(annexureEleven?.candidate_doj),
      
      // Employee Number
      employeeNumber: annexureEleven.employee_number || "",
      
      // Designation
      designation: annexureEleven.designation || "",
      
      // Location
      location: candidateData?.location || "",
      
      // Form statuses
      applicantFormStatus: candidateData?.applicant_form_status || "Pending",
      annexureFormStatus: candidateData?.annexure_eleven_form_status || "Pending",
      
      // Direct fields from root
      jobTitle: candidateData?.job_title || "",
      projectName: candidateData?.project_name || "",
      department: candidateData?.department || "",
      totalExperience: candidateData?.total_experience || "",
      currentCTC: candidateData?.current_ctc || "",
      formStatus: candidateData?.form_status || "",
      profileStatus: candidateData?.profile_status || "",
      kycSteps: candidateData?.kyc_steps || "",
      addDate: candidateData?.add_date || "",
      updatedOn: candidateData?.updated_on || "",
      appliedFrom: candidateData?.applied_from || "",
    };
  }, [candidateData]);

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 8mm 10mm;
      }
      @media print {
        html, body {
          width: 210mm;
          height: 297mm;
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  const cellStyle = {
    border: "1px solid #000",
    fontSize: "10.5px",
    p: 0.5,
    verticalAlign: "top",
  };

  const labelCellStyle = {
    ...cellStyle,
    width: "48%",
    fontWeight: 500,
  };

  const valueCellStyle = {
    ...cellStyle,
    width: "52%",
  };

  // Helper to check if candidate had previous EPF membership
  const hasPreviousEPF = !!extractedData.previousOrg?.previous_pf;

  // Determine salutation based on gender
  const getSalutation = () => {
    const gender = extractedData.gender?.toLowerCase();
    if (gender === "male") return "Mr.";
    if (gender === "female") return "Ms./Mrs.";
    return "Mr./Ms.";
  };

  // Determine which tick box should be checked (Father or Spouse)
  const isFather = candidateData?.applicant_form_data?.isFather;

  const isFatherTick = isFather === true;
  const isSpouseTick = isFather === false;

  return (
    <Box>
      {/* Print Button */}
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      <Box
        ref={formRef}
        sx={{
          backgroundColor: "#fff",
          p: 1.5,
          fontFamily: '"Times New Roman", Times, serif',
          maxWidth: "210mm",
          mx: "auto",
          fontSize: "11px",
          lineHeight: 1.3,
          color: "#000",
        }}
      >
        {/* Header */}
        <Box sx={{ position: "relative", mb: 1, minHeight: "38px" }}>
          <Box sx={{ position: "absolute", left: 0, top: 0 }}>
            <img
              src={
                config?.IMAGE_PATH
                  ? config.IMAGE_PATH + "logo512.png"
                  : "/logo512.png"
              }
              alt="Logo"
              height="36"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/logo512.png";
              }}
            />
          </Box>
          <Typography
            align="center"
            sx={{ fontSize: "13px", fontWeight: "bold", pt: 0.2 }}
          >
            New Form No. 11 – Declaration Form
          </Typography>
          <Typography
            align="center"
            sx={{ fontSize: "9.5px", fontStyle: "italic" }}
          >
            (To be retained by the employer for future reference)
          </Typography>
          <Typography
            align="center"
            sx={{ fontSize: "12px", fontWeight: "bold", mt: 0.3 }}
          >
            EMPLOYEES’ PROVIDENT FUND ORGANISATION
          </Typography>
          <Typography align="center" sx={{ fontSize: "9.5px" }}>
            Employees’ Provident Funds Scheme, 1952 (Paragraph 34 & 57) &
            Employees’ Pension Scheme, 1995 (Paragraph 24)
          </Typography>
          <Typography
            align="center"
            sx={{ fontSize: "9px", mt: 0.2, px: 2 }}
          >
            (Declaration by a person taking up employment in any establishment on
            which EPF Scheme, 1952 and /or EPS, 1995 is applicable)
          </Typography>
        </Box>

        {/* Main Table */}
        <TableContainer sx={{ border: "1px solid #000", mb: 1 }}>
          <Table size="small">
            <TableBody>
              {/* 1. Name */}
              <TableRow>
                <TableCell sx={labelCellStyle}>
                  1. Name of the member
                </TableCell>
                <TableCell sx={valueCellStyle}>
                  {extractedData.fullName.toUpperCase()}
                </TableCell>
              </TableRow>

              {/* 2. Father's / Spouse Name */}
              <TableRow>
              <TableCell sx={labelCellStyle}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  <span>2.</span>
            
                  {/* Father's Name */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 13,
                        height: 13,
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      {isFatherTick ? "✓" : ""}
                    </Box>
            
                    <span>Father's Name</span>
                  </Box>
            
                  {/* Spouse's Name */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 13,
                        height: 13,
                        border: "1px solid #000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      {isSpouseTick ? "✓" : ""}
                    </Box>
            
                    <span>Spouse's Name</span>
                  </Box>
                </Box>
              </TableCell>
            
              <TableCell sx={valueCellStyle}>
                {extractedData.fatherHusbandName}
              </TableCell>
                        </TableRow>

              {/* 3. Date of Birth */}
              <TableRow>
                <TableCell sx={labelCellStyle}>
                  3. Date of Birth : (DD / MM / YYYY )
                </TableCell>
                <TableCell sx={valueCellStyle}>
                  {extractedData.dobFormatted}
                </TableCell>
              </TableRow>

              {/* 4. Gender */}
              <TableRow>
                <TableCell sx={labelCellStyle}>
                  4. Gender : (Male/Female/Transgender)
                </TableCell>
                <TableCell sx={valueCellStyle}>
                  {extractedData.gender.toUpperCase()}
                </TableCell>
              </TableRow>

              {/* 5. Marital Status */}
              <TableRow>
                <TableCell sx={labelCellStyle}>
                  5. Marital Status : (Married/Unmarried/Widow/Widower/Divorcee)
                </TableCell>
                <TableCell sx={valueCellStyle}>
                  {extractedData.maritalStatus.toUpperCase()}
                </TableCell>
              </TableRow>

              {/* 6. Email & Mobile */}
              <TableRow>
                <TableCell sx={labelCellStyle}>
                  6. (a) Email ID :
                  <br />
                  (b) Mobile No. :
                </TableCell>
                <TableCell sx={valueCellStyle}>
                  {extractedData.email || ""}
                  <br />
                  {extractedData.mobileNo || ""}
                </TableCell>
              </TableRow>

              {/* 7. Whether earlier member of EPF */}
              <TableRow>
                <TableCell sx={labelCellStyle}>
                  7. Whether earlier a member of Employees’ Provident Fund
                  Scheme, 1952
                </TableCell>
                <TableCell sx={valueCellStyle}>
                  {hasPreviousEPF ? "Yes" : "No"}
                </TableCell>
              </TableRow>

              {/* 8. Whether earlier member of EPS */}
              <TableRow>
                <TableCell sx={labelCellStyle}>
                  8. Whether earlier a member of Employees’ Pension Scheme, 1995
                </TableCell>
                <TableCell sx={valueCellStyle}>
                  {hasPreviousEPF ? "Yes" : "No"}
                </TableCell>
              </TableRow>

              {/* 9. Previous employment details */}
              <TableRow>
                <TableCell sx={labelCellStyle} colSpan={2}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 500, mb: 0.5 }}>
                    9. Previous employment details: [If Yes to 7 AND/OR 8 above]
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    a) Universal Account Number: {extractedData.previousOrg?.previous_pf || ""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    b) Previous PF Account Number: {extractedData.previousOrg?.previous_pf || ""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    c) Date of exit from previous employment : (DD/MM/YYYY) {extractedData.previousOrg?.previous_to || ""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    d) Scheme Certificate No. (if issued): {""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px" }}>
                    e) Pension Payment Order (PPO) No. (if issued): {""}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* 10. International Worker */}
              <TableRow>
                <TableCell sx={labelCellStyle} colSpan={2}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 500, mb: 0.4 }}>
                    10. a) International Worker: &nbsp;&nbsp;&nbsp; No
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    b) If yes, state country of origin (India/Name of other country): {""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    c) Passport No.: {""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px" }}>
                    d) Validity of passport [(DD/MM/YYYY) to (DD/MM/YYYY)]: {""}
                  </Typography>
                </TableCell>
              </TableRow>

              {/* 11. KYC Details */}
              <TableRow>
                <TableCell sx={labelCellStyle} colSpan={2}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 500, mb: 0.4 }}>
                    11. KYC Details: (attach self attested copies of following KYCs)
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    a) Bank Account No. & IFS Code: {extractedData.accountNo || ""}{extractedData.accountNo && extractedData.branchIfsc ? ` / ${extractedData.branchIfsc}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", mb: 0.3 }}>
                    b) AADHAR Number: {extractedData.aadharNumber || ""}
                  </Typography>
                  <Typography sx={{ fontSize: "10px" }}>
                    c) Permanent Account Number (PAN), if available: {extractedData.panNumber || ""}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* UNDERTAKING */}
        <Typography
          sx={{
            fontSize: "11.5px",
            fontWeight: "bold",
            textAlign: "center",
            mb: 0.6,
          }}
        >
          UNDERTAKING
        </Typography>

        <Typography sx={{ fontSize: "10px", mb: 0.4, textAlign: "justify" }}>
          1) Certified that the particulars are true to the best of my knowledge.
        </Typography>
        <Typography sx={{ fontSize: "10px", mb: 0.4, textAlign: "justify" }}>
          2) I authorize EPFO to use my Aadhar for verification/authentication/eKYC purpose for service delivery.
        </Typography>
        <Typography sx={{ fontSize: "10px", mb: 0.4, textAlign: "justify" }}>
          3) Kindly transfer the funds and service details, if applicable, from the previous PF account as declared above to the present P.F. Account.
          (The transfer would be possible only if the identified KYC detail approved by previous employer has been verified by present employer using his Digital Signature Certificate)
        </Typography>
        <Typography sx={{ fontSize: "10px", mb: 1, textAlign: "justify" }}>
          4) In case of changes in above details, the same will be intimated to employer at the earliest.
        </Typography>

        <Grid container sx={{ mb: 1.5 }}>
          <Grid item xs={4}>
            <Typography sx={{ fontSize: "10.5px" }}>Date:</Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                height: "20px",
                width: "120px",
                mt: 0.3,
                px: 0.5,
              }}
            >
             {formatDate(doj)}
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Typography sx={{ fontSize: "10.5px" }}>Place:</Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                height: "20px",
                width: "120px",
                mt: 0.3,
                px: 0.5,
              }}
            >
              {appliedJob?.job_location || appliedJob?.annexure_eleven_form_data?.place_of_posting || candidateData?.applied_jobs?.find((item) => item?.job_id === candidateData?.job_id)?.proposed_location || candidateData?.location || extractedData.location}
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Typography align="right" sx={{ fontSize: "10.5px" }}>
              Signature of Member
            </Typography>
            <Box
              sx={{
                borderBottom: "1px solid #000",
                height: "28px",
                width: "150px",
                ml: "auto",
                mt: 0.3,
              }}
            />
          </Grid>
        </Grid>

        {/* DECLARATION BY PRESENT EMPLOYER */}
        <Box sx={{ borderTop: "1.5px solid #000", pt: 0.8 }}>
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: "bold",
              textAlign: "center",
              mb: 0.8,
            }}
          >
            DECLARATION BY PRESENT EMPLOYER
          </Typography>

          <Typography sx={{ fontSize: "10px", mb: 0.5, textAlign: "justify" }}>
            A. The member {getSalutation()}{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {extractedData.fullName}
            </Box>{" "}
            has joined on{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {extractedData.joiningDateFormatted}
            </Box>{" "}
            and has been allotted PF Number{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {extractedData.employeeNumber || ""}
            </Box>.
          </Typography>

          <Typography sx={{ fontSize: "10px", mb: 0.4, textAlign: "justify" }}>
            B. In case the person was earlier not a member of EPF Scheme, 1952 and
            EPS, 1995:
          </Typography>
          <Typography sx={{ fontSize: "10px", mb: 0.3, pl: 1.5 }}>
            • (Post allotment of UAN) The UAN allotted for the member is{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              {extractedData.previousOrg?.previous_pf || ""}
            </Box>.
          </Typography>
          <Typography sx={{ fontSize: "10px", mb: 0.3, pl: 1.5 }}>
            • Please Tick the Appropriate Option:
          </Typography>
          <Typography sx={{ fontSize: "9.5px", pl: 3, mb: 0.2 }}>
            □ The KYC details of the above member in the UAN database have not been uploaded
          </Typography>
          <Typography sx={{ fontSize: "9.5px", pl: 3, mb: 0.2 }}>
            □ Have been uploaded but not approved
          </Typography>
          <Typography sx={{ fontSize: "9.5px", pl: 3, mb: 0.5 }}>
            □ Have been uploaded and approved with DSC
          </Typography>

          <Typography sx={{ fontSize: "10px", mb: 0.4, textAlign: "justify" }}>
            C. In case the person was earlier a member of EPF Scheme, 1952 and EPS, 1995:
          </Typography>
          <Typography sx={{ fontSize: "10px", mb: 0.3, pl: 1.5 }}>
            • The above PF Account number/UAN of the member as mentioned in (A) above has been tagged with his/her UAN/Previous Member ID as declared by member.
          </Typography>
          <Typography sx={{ fontSize: "10px", mb: 0.3, pl: 1.5 }}>
            • Please Tick the Appropriate Option:
          </Typography>
          <Typography sx={{ fontSize: "9.5px", pl: 3, mb: 0.2 }}>
            □ The KYC details of the above member in the UAN database have been approved with Digital Signature Certificate.
          </Typography>
          <Typography sx={{ fontSize: "9.5px", pl: 3, mb: 0.8 }}>
            □ As the DSC of establishment are not registered with EPFO, the member has been informed to file physical claim (F-13) for transfer of funds from his previous establishment.
          </Typography>

          <Grid container sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: "10.5px" }}>Date:</Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "22px",
                  width: "130px",
                  mt: 0.3,
                  px: 0.5,
                }}
              >
               {formatDate(doj)}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right" sx={{ fontSize: "10.5px" }}>
                Signature of Employer
              </Typography>
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "28px",
                  width: "120px",
                  ml: "auto",
                  mt: 0.3,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {candidateData?.applicant_form_data?.signature && (
                  <img
                    src={`${config?.IMAGE_PATH || ""}${candidateData.applicant_form_data.signature}`}
                    alt="candidate-signature"
                    style={{
                      maxHeight: "22px",
                      maxWidth: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default EPFOForm11;